// app.transaction — best-effort sequential write group.
//
// IMPORTANT: this is NOT atomic. S3 has no multi-object transactions
// and we don't fake one. What `transaction` actually does:
//
//   1. Execute the provided writes one after another (sequentially).
//   2. If any write fails, STOP, and return a `rollback()` that
//      issues compensating deletes for the writes that DID succeed.
//   3. The caller decides whether to invoke `rollback()` or accept
//      the partial state.
//
// Use this when you want the convenience of "do all these writes,
// fail cleanly if one breaks." Don't use it where true ACID matters
// (banking, inventory, etc.) — for those you need DynamoDB or RDS.

import {
  DeleteObjectCommand,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";

export interface TransactionStep {
  /** Bucket the write lands in. */
  bucket: string;
  /** Key (path) of the object written. */
  key: string;
  /** Body bytes (or string — encoded to UTF-8). */
  body: Uint8Array | string;
  /** Optional content type. Defaults to `application/json` if body
   *  looks like JSON, `application/octet-stream` otherwise. */
  contentType?: string;
  /** Optional `If-None-Match: *` for first-write-wins ordering. */
  ifNoneMatch?: boolean;
  /** Optional `If-Match: <etag>` for optimistic-concurrency writes. */
  ifMatch?: string;
}

export interface TransactionResult {
  /** Steps in execution order, with the result of each. */
  steps: Array<{ step: TransactionStep; status: "succeeded" | "failed" | "skipped"; error?: Error }>;
  /** True when every step succeeded; false on any failure. */
  ok: boolean;
  /** Issues compensating DELETEs for every succeeded step in REVERSE
   *  order. Safe to call multiple times — already-deleted objects
   *  silently 404. */
  rollback: () => Promise<void>;
}

export interface TransactionDeps {
  getClient: () => Promise<S3Client>;
}

export function createTransaction(deps: TransactionDeps) {
  return async function transaction(steps: TransactionStep[]): Promise<TransactionResult> {
    const s3 = await deps.getClient();
    const results: TransactionResult["steps"] = steps.map((step) => ({ step, status: "skipped" }));
    const succeeded: TransactionStep[] = [];
    let firstFailure: Error | null = null;

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i] as TransactionStep;
      if (firstFailure) break;
      try {
        const body = typeof step.body === "string" ? new TextEncoder().encode(step.body) : step.body;
        await s3.send(
          new PutObjectCommand({
            Bucket: step.bucket,
            Key: step.key,
            Body: body,
            ContentType:
              step.contentType ??
              (looksLikeJson(step.body) ? "application/json" : "application/octet-stream"),
            ...(step.ifNoneMatch ? { IfNoneMatch: "*" } : {}),
            ...(step.ifMatch ? { IfMatch: step.ifMatch } : {}),
          } as never),
        );
        (results[i] as { step: TransactionStep; status: "succeeded" | "failed" | "skipped" }).status = "succeeded";
        succeeded.push(step);
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        const r = results[i] as { step: TransactionStep; status: "succeeded" | "failed" | "skipped"; error?: Error };
        r.status = "failed";
        r.error = err;
        firstFailure = err;
      }
    }

    return {
      steps: results,
      ok: !firstFailure,
      async rollback() {
        for (const step of succeeded.slice().reverse()) {
          await s3
            .send(new DeleteObjectCommand({ Bucket: step.bucket, Key: step.key }))
            .catch(() => {});
        }
      },
    };
  };
}

function looksLikeJson(body: unknown): boolean {
  if (typeof body !== "string") return false;
  const trimmed = body.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}
